/**
 * Thin helpers over origami's ERP proxy for the extranet module (contract §6).
 *
 * `GET /api/extranet/erp/registries/{registry_slug}/endpoints/{endpoint_slug}`
 * wraps the raw VEOS payload in `{ success, http_status, data, error, … }`;
 * every `WS2_EA_*` list lives at `data.data` with its total at `data.total`.
 * `SES` is never sent by the browser: the spoof interceptor of
 * `extranetApiClient` sends `X-Erp-Session-Id` and the backend appends it.
 * `perPage` defaults to `9999` server-side on list endpoints, so callers only
 * pass the business parameters (cased exactly as in the fiche).
 *
 * Every domain service (`api/<domain>Service.ts`) goes through
 * {@link fetchErpRows} / {@link fetchErpRecord} / {@link fetchErpList}, which
 * unwrap the envelope and turn any failure — a non-2xx proxy answer or a
 * `success: false` body — into an `Error` carrying the ERP message.
 */

import type { AxiosError } from 'axios';

import { extranetApiClient } from './extranetApiClient';

/** The proxy envelope around a VEOS payload. */
export interface ErpProxyResponse<T> {
  success: boolean;
  http_status?: number;
  data: T;
  error?: { code?: string; message?: string } | null;
}

/** The standard VEOS list envelope (`data` may be omitted on an empty result). */
export interface VeosListEnvelope<T> {
  total?: number;
  perPage?: number;
  page?: number;
  totalPage?: number;
  from?: number | null;
  to?: number | null;
  data?: T[] | null;
}

/** Query parameters forwarded to a VEOS endpoint. */
export type ErpParams = Record<string, string | number | boolean | null | undefined>;

/** Fallback message of a failed ERP call that carried no usable message. */
export const ERP_REQUEST_FAILED_MESSAGE = 'La récupération des données a échoué.';

/** Message of a call attempted without an active VEOS environment. */
export const ERP_NO_REGISTRY_MESSAGE = 'Aucun environnement VEOS actif.';

/** One page of a VEOS list. */
export interface ErpList<T> {
  rows: T[];
  /** `data.total` — falls back to the number of rows returned. */
  total: number;
}

/** Proxy URL of an endpoint of a registry (relative to the `/api` base). */
export function erpEndpointPath(registrySlug: string, endpointSlug: string): string {
  return `/extranet/erp/registries/${encodeURIComponent(
    registrySlug,
  )}/endpoints/${encodeURIComponent(endpointSlug)}`;
}

/** Drop `null` / `undefined` / `''` params so they are not sent at all. */
function cleanParams(params: ErpParams | undefined): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!params) return out;
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') {
      out[key] = value;
    }
  }
  return out;
}

/** Reads a non-empty string message off an unknown error payload. */
function messageOf(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const record = payload as { error?: unknown; message?: unknown };
  const error = record.error;
  if (typeof error === 'object' && error !== null) {
    const nested = (error as { message?: unknown }).message;
    if (typeof nested === 'string' && nested.trim() !== '') return nested;
  }
  if (typeof error === 'string' && error.trim() !== '') return error;
  if (typeof record.message === 'string' && record.message.trim() !== '') {
    return record.message;
  }
  return null;
}

/**
 * Unwrap a proxy envelope: its `data` on success, else an `Error` carrying
 * the ERP message.
 */
export function unwrapErpEnvelope<T>(envelope: ErpProxyResponse<T>): T {
  if (!envelope.success) {
    throw new Error(messageOf(envelope) ?? ERP_REQUEST_FAILED_MESSAGE);
  }
  return envelope.data;
}

/**
 * The ERP message of a failed proxy call — off a `success: false` envelope,
 * or off the body of an axios rejection of the proxy — `null` when it
 * carries none (network error, HTML error page, …).
 */
export function erpMessageOf(source: unknown): string | null {
  if (source instanceof Error) {
    const response = (source as Partial<AxiosError>).response;
    return response === undefined ? null : messageOf(response.data);
  }
  return messageOf(source);
}

/** Normalise an axios rejection of the proxy into an `Error` with the ERP message. */
export function toErpError(error: unknown): Error {
  const axiosError = error as Partial<AxiosError>;
  const fromBody = messageOf(axiosError.response?.data);
  if (fromBody !== null) return new Error(fromBody);
  return error instanceof Error ? error : new Error(ERP_REQUEST_FAILED_MESSAGE);
}

/**
 * GET an endpoint through the proxy and return its unwrapped VEOS payload.
 *
 * @throws Error with the ERP message on any failure.
 */
export async function fetchErpPayload<T>(
  registrySlug: string | null | undefined,
  endpointSlug: string,
  params?: ErpParams,
): Promise<T> {
  if (!registrySlug) throw new Error(ERP_NO_REGISTRY_MESSAGE);
  let envelope: ErpProxyResponse<T>;
  try {
    const response = await extranetApiClient.get<ErpProxyResponse<T>>(
      erpEndpointPath(registrySlug, endpointSlug),
      { params: cleanParams(params) },
    );
    envelope = response.data;
  } catch (error: unknown) {
    throw toErpError(error);
  }
  return unwrapErpEnvelope(envelope);
}

/** GET a VEOS list endpoint: its rows and total. */
export async function fetchErpList<T>(
  registrySlug: string | null | undefined,
  endpointSlug: string,
  params?: ErpParams,
): Promise<ErpList<T>> {
  const payload = await fetchErpPayload<VeosListEnvelope<T> | null>(
    registrySlug,
    endpointSlug,
    params,
  );
  const rows = Array.isArray(payload?.data) ? payload.data : [];
  const total = typeof payload?.total === 'number' ? payload.total : rows.length;
  return { rows, total };
}

/** GET a VEOS list endpoint and return its rows (`[]` when empty). */
export async function fetchErpRows<T>(
  registrySlug: string | null | undefined,
  endpointSlug: string,
  params?: ErpParams,
): Promise<T[]> {
  return (await fetchErpList<T>(registrySlug, endpointSlug, params)).rows;
}

/**
 * GET a single-record endpoint (`FIC_*`, counters, `DEF_LOGIN`) and return
 * its first row, or `null` when VEOS returned none.
 */
export async function fetchErpRecord<T>(
  registrySlug: string | null | undefined,
  endpointSlug: string,
  params?: ErpParams,
): Promise<T | null> {
  const rows = await fetchErpRows<T>(registrySlug, endpointSlug, params);
  return rows[0] ?? null;
}
